import React from "react";
import {
  Avatar,
  Box,
  Chip,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  createTheme,
  useMediaQuery,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import WorkspacePremiumIcon from "@mui/icons-material/EmojiEvents";
import { useParams } from "react-router-dom";
import { useRankingDetailQuery } from "../../queries/useToeicQueries";

const podiumStyles = [
  {
    label: "Champion",
    medal: "1",
    backgroundColor: "#fff7d6",
    color: "#b77900",
    border: "#f6d365",
    shadow: "rgba(183, 121, 0, 0.18)",
  },
  {
    label: "Runner up",
    medal: "2",
    backgroundColor: "#f0ecff",
    color: "#6d4aff",
    border: "#c6b8ff",
    shadow: "rgba(109, 74, 255, 0.16)",
  },
  {
    label: "Third place",
    medal: "3",
    backgroundColor: "#ffe8f0",
    color: "#d81b60",
    border: "#ffb8cf",
    shadow: "rgba(216, 27, 96, 0.14)",
  },
  {
    label: "Top 4",
    medal: "4",
    backgroundColor: "#e8fbff",
    color: "#0087a5",
    border: "#a7e9f6",
    shadow: "rgba(0, 135, 165, 0.12)",
  },
  {
    label: "Top 5",
    medal: "5",
    backgroundColor: "#fff0db",
    color: "#d56b00",
    border: "#ffc57a",
    shadow: "rgba(213, 107, 0, 0.12)",
  },
];

const getRankStyle = (index) =>
  podiumStyles[index] || {
    label: `Rank ${index + 1}`,
    medal: "#",
    backgroundColor: "#f5f8fb",
    color: "#5f6b7a",
    border: "#d7e0ea",
    shadow: "rgba(15, 23, 42, 0.08)",
  };

function RankingDetail() {
  const { id } = useParams();
  const { data } = useRankingDetailQuery(id);
  const answerInfo = data?.answerInfo || {};
  const rankings = data?.rankings || [];

  const theme = createTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const topRankings = rankings.slice(0, 5);

  return (
    <Container
      sx={{
        width: "100%",
        maxWidth: "1200px !important",
        mt: 4,
        mb: 6,
        px: { xs: 1.5, sm: 2, md: 3 },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {answerInfo && (
        <Box sx={{ textAlign: "center", mb: 4, width: "100%" }}>
          <Box
            sx={{
              width: 82,
              height: 82,
              mx: "auto",
              mb: 1.5,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              backgroundColor: "#fff7d6",
              border: "1px solid #f6d365",
            }}
          >
            <EmojiEventsIcon sx={{ fontSize: 46, color: "#7a3f00" }} />
          </Box>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            sx={{ fontWeight: 700, color: "#07111f" }}
          >
            Ranking of {answerInfo.name}
          </Typography>
          <Typography sx={{ mt: 1, color: "#5f6b7a" }}>
            Top scores are ranked by each user's best attempt.
          </Typography>
        </Box>
      )}

      {rankings.length > 0 ? (
        <>
          <Box
            sx={{
              width: "100%",
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : "repeat(auto-fit, minmax(170px, 1fr))",
              gap: 2,
              mb: 3,
            }}
          >
            {topRankings.map((ranking, index) => {
              const rankStyle = getRankStyle(index);

              return (
                <Paper
                  key={`${ranking.userID}-${index}`}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${rankStyle.border}`,
                    textAlign: "center",
                    backgroundColor: rankStyle.backgroundColor,
                    boxShadow: `0 16px 36px ${rankStyle.shadow}`,
                    transform:
                      !isMobile && index === 0 ? "translateY(-8px)" : "none",
                  }}
                >
                  <Avatar
                    sx={{
                      width: index === 0 ? 70 : 58,
                      height: index === 0 ? 70 : 58,
                      mx: "auto",
                      mb: 1,
                      backgroundColor: rankStyle.backgroundColor,
                      color: rankStyle.color,
                      border: `2px solid ${rankStyle.border}`,
                      fontWeight: 700,
                      fontSize: index === 0 ? 24 : 20,
                    }}
                  >
                    <EmojiEventsIcon sx={{ mr: 0.5 }} />
                    {rankStyle.medal}
                  </Avatar>
                  <Chip
                    size="small"
                    icon={
                      index < 3 ? <EmojiEventsIcon /> : <WorkspacePremiumIcon />
                    }
                    label={rankStyle.label}
                    sx={{
                      mb: 1,
                      fontWeight: 800,
                      color: rankStyle.color,
                      backgroundColor: "#ffffff",
                      border: `1px solid ${rankStyle.border}`,
                      "& .MuiChip-icon": { color: rankStyle.color },
                    }}
                  />
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#07111f",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={ranking.userName}
                  >
                    {ranking.userName}
                  </Typography>
                  <Typography sx={{ mt: 0.5, color: rankStyle.color, fontWeight: 700 }}>
                    {ranking.highestScoreRecord.history_score} pts
                  </Typography>
                </Paper>
              );
            })}
          </Box>

          <TableContainer
            sx={{
              width: "100%",
              borderRadius: 2,
              border: "1px solid #e5edf5",
              boxShadow: "0 18px 42px rgba(15, 23, 42, 0.08)",
            }}
            component={Paper}
          >
            <Table>
              <TableHead sx={{ backgroundColor: "#2196f3" }}>
              <TableRow>
                <TableCell sx={{ color: "#ffffff", fontWeight: "bold" }}>
                  Rank
                </TableCell>
                <TableCell sx={{ color: "#ffffff", fontWeight: "bold" }}>
                  Name
                </TableCell>
                <TableCell sx={{ color: "#ffffff", fontWeight: "bold" }}>
                  Score
                </TableCell>
                <TableCell sx={{ color: "#ffffff", fontWeight: "bold" }}>
                  Day Completed
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rankings.map((ranking, index) => {
                const rankStyle = getRankStyle(index);

                return (
                  <TableRow
                    key={`${ranking.userID}-${index}`}
                    sx={{
                      backgroundColor:
                        index < 3
                          ? rankStyle.backgroundColor
                          : index % 2 === 0
                          ? "#f5f8fb"
                          : "#ffffff",
                      "&:hover": {
                        backgroundColor: "#e3f2fd",
                      },
                    }}
                  >
                    <TableCell>
                      <Chip
                        size="small"
                        icon={
                          index < 3 ? (
                            <EmojiEventsIcon />
                          ) : (
                            <WorkspacePremiumIcon />
                          )
                        }
                        label={`#${index + 1}`}
                        sx={{
                          minWidth: 62,
                          fontWeight: 700,
                          color: rankStyle.color,
                          backgroundColor: rankStyle.backgroundColor,
                          border: `1px solid ${rankStyle.border}`,
                          "& .MuiChip-icon": { color: rankStyle.color },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: index < 5 ? 800 : 500 }}>
                      {ranking.userName}
                    </TableCell>
                    <TableCell sx={{ color: "#2196f3", fontWeight: 700 }}>
                      {ranking.highestScoreRecord.history_score}
                    </TableCell>
                    <TableCell>
                      {new Date(
                        ranking.highestScoreRecord.createdAt
                      ).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </TableContainer>
        </>
      ) : (
        <Typography
          variant={isMobile ? "h6" : "h5"}
          sx={{
            width: "100%",
            p: 3,
            textAlign: "center",
            borderRadius: 2,
            color: "#5f6b7a",
            backgroundColor: "#f5f8fb",
            border: "1px dashed #cbd5e1",
          }}
        >
          THIS TEST HAS NOT BEEN DONE BY ANY USER
        </Typography>
      )}
    </Container>
  );
}

export default RankingDetail;
